import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import SceneView from "./SceneView";
import InfoDialog from "./InfoDialog";
import { photoStartTime } from "./tour";
import type { PresentationScene } from "./types";

const PHASE_LABELS: Record<string, string> = {
  hold: "pohled z fotografie",
  retreat: "vzdalujeme se",
  orbit: "přelétáme k dalšímu pohledu",
  approach: "vstupujeme do fotografie",
};

export default function App() {
  const [data, setData] = useState<PresentationScene | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [phase, setPhase] = useState("hold");
  const [infoOpen, setInfoOpen] = useState(true);
  const elapsedRef = useRef(0);

  useEffect(() => {
    const source = new URL("data/scene.json", document.baseURI).href;
    void fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<PresentationScene>;
      })
      .then(setData)
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : "neznámá chyba");
      });
  }, []);

  const center = useMemo(() => {
    if (!data) return new THREE.Vector3();
    return new THREE.Box3(
      new THREE.Vector3().fromArray(data.model.bounds[0]),
      new THREE.Vector3().fromArray(data.model.bounds[1]),
    ).getCenter(new THREE.Vector3());
  }, [data]);

  const selectPhoto = useCallback(
    (index: number) => {
      if (!data) return;
      const normalized = THREE.MathUtils.euclideanModulo(
        index,
        data.photos.length,
      );
      elapsedRef.current = photoStartTime(data.photos, center, normalized);
      setPhotoIndex(normalized);
      setPhase("hold");
      setPlaying(true);
    },
    [center, data],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (infoOpen || event.defaultPrevented) return;
      if (event.code === "Space") {
        event.preventDefault();
        setPlaying((value) => !value);
      } else if (event.key === "ArrowRight") {
        selectPhoto(photoIndex + 1);
      } else if (event.key === "ArrowLeft") {
        selectPhoto(photoIndex - 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [infoOpen, photoIndex, selectPhoto]);

  if (error) {
    return (
      <main className="load-state">
        <p className="eyebrow">Rovnováha</p>
        <h1>Prezentaci se nepodařilo načíst.</h1>
        <p>{error}</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="load-state">
        <span className="loader" />
        <p>Načítáme prostorovou rekonstrukci…</p>
      </main>
    );
  }

  const photo = data.photos[photoIndex];
  return (
    <main className="presentation-shell">
      <SceneView
        data={data}
        playing={playing}
        elapsedRef={elapsedRef}
        onTourUpdate={(nextPhoto, nextPhase) => {
          setPhotoIndex(nextPhoto);
          setPhase(nextPhase);
        }}
      />

      <header className="masthead">
        <div>
          <p className="eyebrow">Prostorová rekonstrukce by bkralik</p>
          <h1>{data.title}</h1>
          <p className="artist">{data.artist}</p>
        </div>
        <div className="fit-signature">
          <span>7 pohledů</span>
          <span>4 spline hrany</span>
          <span>jeden prostorový tvar</span>
        </div>
      </header>

      <aside className="tour-controls" aria-label="Ovládání průletu">
        <button type="button" onClick={() => setPlaying((value) => !value)}>
          <span
            className={playing ? "pause-icon" : "play-icon"}
            aria-hidden="true"
          />
          {playing ? "Pozastavit" : "Pokračovat"}
        </button>
        <button type="button" onClick={() => selectPhoto(photoIndex + 1)}>
          Další pohled <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-controls="presentation-info"
          aria-expanded={infoOpen}
          onClick={() => setInfoOpen(true)}
        >
          Info
        </button>
      </aside>

      <section className="photo-caption" aria-live="polite">
        <div className="counter">{String(photoIndex + 1).padStart(2, "0")}</div>
        <div>
          <p className="phase">{PHASE_LABELS[phase] ?? phase}</p>
          <h2>{photo.title}</h2>
          <p className="caption-note">
            Fotografie leží v prostoru podle kamery nalezené společným fitem.
          </p>
        </div>
      </section>

      <nav className="photo-navigation" aria-label="Pohledy rekonstrukce">
        {data.photos.map((item, index) => (
          <button
            key={item.id}
            type="button"
            className={index === photoIndex ? "active" : ""}
            aria-label={`Přejít na pohled ${index + 1}: ${item.title}`}
            aria-current={index === photoIndex ? "true" : undefined}
            onClick={() => selectPhoto(index)}
          >
            <span />
          </button>
        ))}
      </nav>

      <footer className="interaction-hint">
        {playing
          ? "mezerník pozastaví průlet"
          : "tažením otáčejte · kolečkem přibližujte"}
      </footer>
      <InfoDialog
        data={data}
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
    </main>
  );
}
